import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DialogModule } from 'primeng/dialog';

/** Whether the agent acts on its own or waits for a recruiter to sign off. */
export type AiGalleryAutonomy = 'autonomous' | 'approval';

export interface AiGalleryAgent {
  title: string;
  /** Stage of the recruiting flow this agent belongs to. */
  category: string;
  description: string;
  /** icon-library.svg (Lucide) symbol id. */
  icon: string;
  /** Short capability chips shown above the autonomy pill. */
  tags: string[];
  autonomy: AiGalleryAutonomy;
  /** Autonomy pill copy — the approval wording differs per agent. */
  autonomyLabel: string;
}

@Component({
  selector: 'app-ai-gallery',
  imports: [DialogModule],
  templateUrl: './ai-gallery.html',
  styleUrl: './ai-gallery.css',
})
export class AiGallery {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  /** Emits the selected agent when the user clicks a card. */
  @Output() agentSelected = new EventEmitter<AiGalleryAgent>();

  /** Ordered by where the agent sits in the recruiting flow, not alphabetically. */
  readonly agents: AiGalleryAgent[] = [
    {
      title: 'Ask Anything',
      category: 'Reporting & Insight',
      description:
        "Answers plain-language questions about your own data — job creation trends, applicants, submissions, placements, revenue — with a chart and a written explanation of what's driving the trend. Gets sharper the more it's used.",
      icon: 'message-circle-question-mark',
      tags: ['BigQuery', 'Trend analysis', 'Charts + narrative'],
      autonomy: 'autonomous',
      autonomyLabel: 'Fully autonomous',
    },
    {
      title: 'Search & Match',
      category: 'Candidate Matching',
      description:
        'Give it a job ID, or just ask — "who matches the job we just posted?" — and it works with Ask Anything to identify the role, then returns a ranked list of the best-fit candidates for it.',
      icon: 'search-check',
      tags: ['Job ID input', 'Conversational input', 'Ranked results'],
      autonomy: 'autonomous',
      autonomyLabel: 'Fully autonomous',
    },
    {
      title: 'Email Agent',
      category: 'Candidate Outreach',
      description:
        'Takes the candidates that Search & Match surfaces and drafts personalized outreach emails, ready to go out the moment a recruiter signs off.',
      icon: 'mail',
      tags: ['Personalized drafts', 'Bulk-ready'],
      autonomy: 'approval',
      autonomyLabel: 'Recruiter approval before sending',
    },
    {
      title: 'Pipeline Agent',
      category: 'Candidate Pipelining',
      description:
        'Tags matched candidates to a job to hold them for later consideration — a lightweight way to keep good candidates warm without committing them to an active process yet.',
      icon: 'funnel',
      tags: ['Top-of-funnel', 'Auto-tagging'],
      autonomy: 'autonomous',
      autonomyLabel: 'Fully autonomous',
    },
    {
      title: 'Submission Agent',
      category: 'Active Engagement',
      description:
        'Moves candidates from consideration into an active submission on the job — the point where they become a committed, actively-engaged candidate rather than a pipeline hold.',
      icon: 'send',
      tags: ['Active status', 'Submissions list'],
      autonomy: 'approval',
      autonomyLabel: 'Recruiter approval before submitting',
    },
    {
      title: 'Task Manager',
      category: 'Follow-through',
      description:
        'Once a candidate is submitted, automatically creates the right follow-up tasks so nothing slips through the cracks for the recruiting team.',
      icon: 'list-checks',
      tags: ['Auto-created', 'Post-submission'],
      autonomy: 'autonomous',
      autonomyLabel: 'Fully autonomous',
    },
    {
      title: 'Notes Agent',
      category: 'Follow-through',
      description:
        "Writes structured notes on the candidate record after submission — capturing context automatically so the recruiter's history stays complete without manual entry.",
      icon: 'notebook-pen',
      tags: ['Auto-written', 'Post-submission'],
      autonomy: 'autonomous',
      autonomyLabel: 'Fully autonomous',
    },
    {
      title: 'Interview Scheduling',
      category: 'Follow-through',
      description:
        'Sends calendar invites and coordinates interview logistics with candidates — and can run the first-round screen itself with voice AI interviews.',
      icon: 'calendar-clock',
      tags: ['Calendar invites', 'Voice AI screening'],
      autonomy: 'approval',
      autonomyLabel: 'Confirmation before finalizing',
    },
  ];

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  select(agent: AiGalleryAgent): void {
    this.agentSelected.emit(agent);
  }
}
